type EventValue = {
  type?: string;
  value?: string;
};

// Duck-typed on purpose — mirrors the sibling filters' SentryEventLike so
// tests can pass plain object literals with no @sentry/* mocking.
type SentryEventLike = {
  message?: string;
  exception?: {
    values?: EventValue[];
  };
};

// Apollo invariant codes raised by QueryManager.cancelPendingFetches, which is
// the only thing that rejects an in-flight query's promise on teardown:
//   35 — QueryManager.stop()      "QueryManager stopped while query was in flight"
//   42 — QueryManager.clearStore() "Store reset while query was in flight
//                                   (not completed in link chain)"
// Verified against node_modules/@apollo/client/core/QueryManager.js in 3.14.0.
// Bump-sensitive: Apollo renumbers these codes between minors, so the
// non-minified message match below is the durable half of this check.
const TEARDOWN_INVARIANT_CODES = new Set([35, 42]);

const TEARDOWN_INVARIANT_MESSAGES = [
  'Store reset while query was in flight',
  'QueryManager stopped while query was in flight',
];

// Minified builds replace the message with a code and a docs link, e.g.
// https://go.apollo.dev/c/err#{"version":"3.14.0","message":42,"args":[]}
// (URL-encoded in the error text). Everything after `err#` is the payload.
const INVARIANT_DOCS_MARKER = 'go.apollo.dev/c/err#';

const extractInvariantCode = (text: string): number | undefined => {
  const markerIndex = text.indexOf(INVARIANT_DOCS_MARKER);
  if (markerIndex === -1) return undefined;

  const fragment = text.slice(markerIndex + INVARIANT_DOCS_MARKER.length);
  try {
    const payload = JSON.parse(decodeURIComponent(fragment)) as {
      message?: unknown;
    };
    return typeof payload.message === 'number' ? payload.message : undefined;
  } catch {
    // Truncated or otherwise unparseable payload — treat as not a match rather
    // than guessing, so an unrelated error is never dropped.
    return undefined;
  }
};

const isTeardownInvariant = (text: string): boolean => {
  if (TEARDOWN_INVARIANT_MESSAGES.some((message) => text.includes(message))) {
    return true;
  }
  const code = extractInvariantCode(text);
  return code !== undefined && TEARDOWN_INVARIANT_CODES.has(code);
};

/**
 * Drops the `Invariant Violation: An error occurred!` events Apollo raises when
 * the store is torn down while queries are in flight (Sentry FRONTEND-2Z4,
 * TA-10850).
 *
 * Every `clearStore()` / `stop()` call site in this app is a logout path —
 * MyAccount.web, MyProfile.web, MyProfile, Settings, and `clearCache()` in
 * useApolloClient (which also runs on the 401-triggered logout). Tearing the
 * store down is the last thing logout does, so any query still in flight has
 * its promise rejected by `QueryManager.cancelPendingFetches`.
 *
 * Those rejections belong to the individual in-flight operations, not to the
 * `clearStore()` promise the logout handler awaits — which is why the
 * `try/catch` around `await client?.clearStore()` in MyAccount.web.tsx does not
 * catch them, and why they arrive as `onunhandledrejection`. Reordering
 * `stop()` before `clearStore()` does not help either: both funnel through
 * `cancelPendingFetches` and would only swap invariant 42 for 35.
 *
 * There is no signal here. The session is over, the store is being discarded on
 * purpose, and the stack points at the logout handler rather than at whichever
 * operation was unlucky enough to still be open.
 *
 * Deliberately narrow: only the two teardown invariant codes, matched by their
 * non-minified message text or the code in Apollo's docs-link payload. Any
 * other Apollo invariant — including a genuine cache/normalisation violation —
 * still reports.
 */
export const shouldDropApolloTeardownInvariant = (
  event: SentryEventLike
): boolean => {
  const candidates = [
    event.message,
    ...(event.exception?.values?.map((value) => value.value ?? '') ?? []),
  ];
  return candidates.some((text) => !!text && isTeardownInvariant(text));
};
